import { useState } from 'react';
import { supabase } from '../../services/supabaseClient.ts';
import { AI_FIXER_V2 } from '../../constants/features.ts';
import type { ToolTarget, FixResponse } from '../engine/LLMProvider.ts';

interface GenerateFixesParams {
  scanId: string;
  toolTarget: ToolTarget;
}

interface UseGenerateFixesReturn {
  generateFixes: (params: GenerateFixesParams) => Promise<FixResponse>;
  isGenerating: boolean;
  error: string | null;
  clearError: () => void;
}

export function useGenerateFixes(): UseGenerateFixesReturn {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateFixes = async ({ scanId, toolTarget }: GenerateFixesParams): Promise<FixResponse> => {
    console.log(`[useGenerateFixes] Generating fixes for scan ${scanId} with tool ${toolTarget}`);
    
    setIsGenerating(true);
    setError(null);

    try {
      // Get the current session for authentication
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Authentication required to generate fixes');
      }

      // Route to v1 or v2 based on feature flag
      const endpoint = AI_FIXER_V2 ? 'generate-fixes-v2' : 'generate-fixes';
      console.log(`[useGenerateFixes] Making request to ${endpoint} function (v${AI_FIXER_V2 ? '2' : '1'})`);

      // Call the appropriate Edge Function
      const { data, error: functionError } = await supabase.functions.invoke(endpoint, {
        body: {
          scan_id: scanId,
          tool_target: toolTarget
        }
      });

      if (functionError) {
        console.error('[useGenerateFixes] Function error:', functionError);
        throw new Error(`Failed to generate fixes: ${functionError.message}`);
      }

      if (!data) {
        throw new Error('No data returned from fix generation');
      }

      // Handle v1 vs v2 response formats
      let fixResponse: FixResponse;
      
      if (AI_FIXER_V2) {
        // V2 response format: { fixes: [...], metadata: {...} }
        if (!Array.isArray(data.fixes) || !data.metadata) {
          console.error('[useGenerateFixes] Invalid v2 response structure:', data);
          throw new Error('Invalid response from v2 fix generation service');
        }
        
        console.log(`[useGenerateFixes] V2 response: ${data.fixes.length} fixes, ${data.metadata.duplicates_removed} duplicates removed`);
        console.log(`[useGenerateFixes] V2 token usage: ${data.metadata.token_usage.total} tokens (${Math.round((1 - data.metadata.token_usage.total / 135000) * 100)}% reduction)`);
        
        // Convert v2 to v1 format for frontend compatibility
        fixResponse = {
          tool_target: toolTarget,
          fixes: data.fixes.map((fix: any) => ({
            finding_id: fix.finding_id,
            file_path: fix.file_path,
            line: fix.line_number,
            needs_source: Boolean(fix.needs_source),
            patch_prompt: fix.patch_prompt || `Fix needed for ${fix.title}`,
            explanation: fix.description
          })),
          errors: [] // V2 errors are handled differently
        };
      } else {
        // V1 response format: { tool_target: "...", fixes: [...], errors: [...] }
      if (!data.tool_target || !Array.isArray(data.fixes) || !Array.isArray(data.errors)) {
          console.error('[useGenerateFixes] Invalid v1 response structure:', data);
          throw new Error('Invalid response from v1 fix generation service');
        }
        
        fixResponse = data as FixResponse;
      }

      console.log(`[useGenerateFixes] Successfully generated ${fixResponse.fixes.length} fixes`);

      return fixResponse;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate fixes';
      console.error('[useGenerateFixes] Error:', errorMessage);
      setError(errorMessage);
      throw err;
    } finally {
      setIsGenerating(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return {
    generateFixes,
    isGenerating,
    error,
    clearError
  };
} 